import type { Participant, ShareKind, SfuPublication, SignalPayload } from '@shared/protocol'
import { PeerMesh, type PeerConnectionState } from '@/lib/mesh'
import type { ReceiveLimit } from '@/lib/receiveQuality'
import type { TrackSample } from '@/lib/streamQuality'
import type { ShareEncoder } from '@/lib/videoEncoder'

export type { PeerConnectionState }

/** Uma transmissao local pronta pra ir pro ar. */
export interface LocalStream {
    kind: ShareKind
    stream: MediaStream
}

/**
 * Contrato que a sala usa pra mover midia, independente do caminho escolhido:
 * malha P2P (`mesh`) ou SFU da Cloudflare (`sfu`).
 */
export interface StreamTransport {
    /** Participantes atuais, sem contar voce. */
    syncPeers(peers: Participant[]): void
    /**
     * Publica o conjunto atual de transmissoes (tela, camera, as duas ou
     * nenhuma). No modo SFU devolve as coordenadas de cada uma pros outros
     * assinarem; no mesh devolve um mapa vazio — la o vinculo e o id da stream.
     */
    setLocalStreams: (streams: LocalStream[]) => Promise<Map<ShareKind, SfuPublication>>
    /** Payload vindo de outro participante (so o mesh usa). */
    handleSignal(from: string, data: SignalPayload): void
    setMaxBitrate(bitrate: number): void
    /**
     * Quem codifica o video que sai daqui (placa de video ou processador).
     *
     * A malha troca na hora, sem renegociar. O SFU so guarda: a troca sai na
     * proxima `setLocalStreams`, que republica o video com o codec novo.
     */
    setEncoder(encoder: ShareEncoder): void
    /** The `peerId:kind` shares this viewer wants; the rest isn't subscribed to, decoded or sent. */
    setWatched(keys: ReadonlySet<string>): void
    /** The person's cap on the whole upload, in bits/s; `0` = none. */
    setUploadLimit(bitrate: number): void
    /** Asks the sender for a lighter copy of one stream; mesh only for now. */
    requestReceiveLimit(peerId: string, streamId: string, limit: ReceiveLimit | null): void
    /**
     * Medidas de video por id de trilha, lidas de `getStats()`.
     *
     * Vive no transporte porque so ele tem as `RTCPeerConnection` — a UI so ve
     * `MediaStream`. A chave e o id da trilha justamente pra UI conseguir
     * casar uma medida com o tile certo sem saber qual transporte esta no ar.
     */
    collectStats(): Promise<Map<string, TrackSample>>
    close(): void
}

export interface MeshTransportOptions {
    selfId: string
    iceServers: RTCIceServer[]
    sendSignal: (to: string, data: SignalPayload) => void
    /** O `streamId` diz qual transmissao daquele peer acabou de chegar. */
    onStream: (peerId: string, streamId: string, stream: MediaStream) => void
    onPeerGone: (peerId: string) => void
    /**
     * Estado da conexao com aquele peer. Os dois transportes reportam o MESMO
     * vocabulario justamente pra UI nao precisar saber qual esta ativo.
     */
    onPeerState?: (peerId: string, state: PeerConnectionState) => void
    createConnection?: (config: RTCConfiguration) => RTCPeerConnection
    onDemand?: boolean
    onWatchers?: (counts: Map<ShareKind, number>) => void
}

/** Adapta a `PeerMesh` (que pensa em ids) ao contrato de transporte. */
export function createMeshTransport(options: MeshTransportOptions): StreamTransport {
    const mesh = new PeerMesh(options)

    return {
        syncPeers: (peers) => mesh.syncPeers(peers.map((peer) => peer.id)),
        setLocalStreams: async (streams) => {
            mesh.setLocalStreams(
                streams.map((item) => item.stream),
                new Map(streams.map((item) => [item.stream.id, item.kind]))
            )
            // No mesh nao ha coordenadas a anunciar: o que casa video com tipo
            // e o id da stream, que o hook manda junto das publicacoes.
            return new Map()
        },
        handleSignal: (from, data) => void mesh.handleSignal(from, data),
        setMaxBitrate: (bitrate) => mesh.setMaxBitrate(bitrate),
        setEncoder: (encoder) => mesh.setEncoder(encoder),
        setWatched: (keys) => mesh.setWatched(keys),
        setUploadLimit: (bitrate) => mesh.setUploadLimit(bitrate),
        requestReceiveLimit: (peerId, streamId, limit) => mesh.requestReceiveLimit(peerId, streamId, limit),
        collectStats: () => mesh.collectStats(),
        close: () => mesh.close()
    }
}
